import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { RemakeResult, VariantEdits } from '@/lib/api/amazon-listing.types'
import type { CompiledRules } from '@/hooks/validation.types'
import { RemakeVariantView } from './RemakeVariantView'

interface RemakeVariantTabsProps {
  result: RemakeResult
  edits: Record<string, VariantEdits>
  onEditChange: (vi: number, field: string, value: string | string[]) => void
  onResetField: (vi: number, field: string) => void
  rules: CompiledRules | undefined
}

const STYLE_KEYS: Record<string, string> = {
  jury: 'variant.jury',
  seo: 'variant.seo',
  psychology: 'variant.psychology',
  specialist: 'variant.specialist',
  storyteller: 'variant.storyteller',
}

export function RemakeVariantTabs({ result, edits, onEditChange, onResetField, rules }: RemakeVariantTabsProps) {
  const { t } = useTranslation()
  const variants = result.variants ?? []
  const [activeTab, setActiveTab] = useState(result.jury_index ?? 0)

  const activeVariant = variants[activeTab]
  const variantEdits = edits[String(activeTab)] ?? {}

  if (variants.length === 0) return null

  return (
    <div className="space-y-4">
      <div className="flex gap-1 overflow-x-auto border-b border-neutral-200 dark:border-neutral-700">
        {variants.map((v, i) => {
          const isJury = i === result.jury_index
          const isActive = i === activeTab
          const styleKey = STYLE_KEYS[v.agent_style] ?? v.agent_style
          return (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
                isActive
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 font-medium'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
              }`}
            >
              {t(`remake.${styleKey}`)}
              {isJury && (
                <span className="ml-1.5 px-1.5 py-0.5 text-2xs rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                  {t('remake.recommended')}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {activeVariant && (
        <RemakeVariantView
          variant={activeVariant}
          variantIndex={activeTab}
          edits={variantEdits}
          onEditChange={onEditChange}
          onResetField={onResetField}
          rules={rules}
        />
      )}
    </div>
  )
}
